/* eslint-disable max-lines */
import { computed, nextTick, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

import type { ChartType, VisualizationTypeDefinition } from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type { ReportFilter } from '../models/filter.types'
import type { ReportRow } from '../models/report.types'
import type { ReportTableConfigV1, ReportTableSortV1 } from '../models/tableConfig.types'
import type {
  ReportTableCustomization,
  ReportTableSavedViewDraft,
  ReportTableSavedViewV1,
  ReportTableType,
} from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import { isReportFilterDropdownableColumn } from '../service/reportColumnKind'
import {
  buildReportViewConfigUpsert,
  reportViewConfigToTablePreference,
} from '../service/reportViewConfig'
import {
  getAvailableReportTableTypes,
  resolveMatrixPivotAssignment,
  resolveTrendAssignment,
} from '../service/tablePresentation'

import { useDataVisibleTableState } from './useDataVisibleTableState'
import { useLocalReportTableViews } from './useLocalReportTableViews'
import { useReportFilterDropdownables } from './useReportFilterDropdownables'
import { useReportTablePresentationState } from './useReportTablePresentationState'
import { useReportViewConfigs } from './useReportViewConfigs'
import { useSavedReportFilters } from './useSavedReportFilters'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useConfirm, useToast, type SortState, type TableColumn } from '@/shared/ui'

/**
 * Reserved id for the backend stable view auto-applied on load. It is a UI-only reflection in the
 * saved-view Select and is never persisted as a named `namedTableViewsV1` record (contract 08 §7).
 */
const STABLE_VIEW_SELECT_ID = '__stable-view__'
const DEFAULT_TABLE_TYPES: readonly ReportTableType[] = ['table', 'matrix_table', 'trend_table']

export interface UseDataVisibleTableExperienceOptions {
  id: MaybeRefOrGetter<string>
  reportId: MaybeRefOrGetter<number>
  partitionName: MaybeRefOrGetter<string | undefined>
  sortField: MaybeRefOrGetter<string | undefined>
  sortOrder: MaybeRefOrGetter<1 | -1>
  tableConfig: MaybeRefOrGetter<ReportTableConfigV1 | undefined>
  columns: MaybeRefOrGetter<ReportColumn[]>
  defaultTableType: MaybeRefOrGetter<ReportTableType>
  defaultBreakdown: MaybeRefOrGetter<string | null>
  matrixDimension?: MaybeRefOrGetter<string | null>
  pivotMetric?: MaybeRefOrGetter<string | null>
  allowedTableTypes: MaybeRefOrGetter<readonly ReportTableType[] | undefined>
  namedViews: MaybeRefOrGetter<readonly ReportTableSavedViewV1[]>
  userFilters: MaybeRefOrGetter<readonly ReportFilter[]>
  enabled: MaybeRefOrGetter<boolean>
  filtersEnabled: MaybeRefOrGetter<boolean>
  catalogSettled: MaybeRefOrGetter<boolean>
  catalogReady: MaybeRefOrGetter<boolean>
  catalogDefinitions: MaybeRefOrGetter<readonly VisualizationTypeDefinition[]>
  dimensions: MaybeRefOrGetter<readonly string[]>
  metrics: MaybeRefOrGetter<readonly string[]>
  activeChart: MaybeRefOrGetter<ChartType>
  onSort: (value: ReportTableSortV1 | null) => void
  onColumnOrder: (value: string[]) => void
  onColumnWidths: (value: Record<string, number>) => void
  onFilters: (value: ReportFilter[]) => void
  onTableType: (value: ReportTableType) => void
  onBreakdown: (value: string | null) => void
  onFrequency: (value: TrendTableFrequency) => void
  onRemoteActiveChart: (value: string | null) => void
}

function cloneFilters(filters: readonly ReportFilter[]): ReportFilter[] {
  return filters.map((filter) => ({
    ...filter,
    value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
  }))
}

/** Table-only UI and persistence orchestration kept out of the DataVisible render shell. */
export function useDataVisibleTableExperience(options: UseDataVisibleTableExperienceOptions) {
  const { t } = useI18n()
  const toast = useToast()
  const confirm = useConfirm()
  const workspace = useWorkspace()
  const customizeOpen = ref(false)
  // Auto-applied backend stable view reflection + a guard so a late list resolve never overrides an
  // explicit saved-view choice (or explicit clear) the user made before the fetch settled (ORP-19312).
  const stableViewSelected = ref(false)
  const userSelectedView = ref(false)
  const filterDraft = ref(cloneFilters(toValue(options.userFilters)))
  const activeUserFilters = ref(cloneFilters(toValue(options.userFilters)))
  const table = useDataVisibleTableState({
    id: options.id,
    reportId: options.reportId,
    partitionName: options.partitionName,
    configuredSortField: options.sortField,
    configuredSortOrder: options.sortOrder,
    configuredTable: options.tableConfig,
    columns: options.columns,
  })
  const presentation = useReportTablePresentationState({
    id: options.id,
    partitionName: options.partitionName,
    columns: table.columns,
    requiredFields: computed(() => table.config.value.requiredFields),
    defaultTableType: options.defaultTableType,
    defaultBreakdownField: options.defaultBreakdown,
  })
  const presentedColumns = computed<ReportColumn[]>(() => {
    const visible = new Set(presentation.visibleFields.value)
    const frozen = new Set(presentation.frozenFields.value)
    return table.columns.value.map((column) => ({
      ...(column as ReportColumn),
      invisible: !visible.has(column.field),
      frozen: frozen.has(column.field),
    }))
  })
  const savedFilters = useSavedReportFilters(options.filtersEnabled)
  const filterDropdownables = useReportFilterDropdownables({
    reportId: options.reportId,
    fields: computed(() =>
      toValue(options.columns)
        .filter(isReportFilterDropdownableColumn)
        .map((column) => column.field),
    ),
    enabled: options.filtersEnabled,
  })
  const viewConfigs = useReportViewConfigs(options.enabled)
  const localViews = useLocalReportTableViews({
    id: options.id,
    partitionName: options.partitionName,
    seedViews: options.namedViews,
  })
  const matrixOptions = computed(() => {
    const matrixDimension = toValue(options.matrixDimension)
    const pivotMetric = toValue(options.pivotMetric)
    return {
      breakdownField: presentation.breakdownField.value,
      ...(matrixDimension === undefined ? {} : { matrixDimension }),
      ...(pivotMetric === undefined ? {} : { pivotMetric }),
    }
  })
  const matrixAssignment = computed(() =>
    resolveMatrixPivotAssignment(presentedColumns.value, matrixOptions.value),
  )
  const trendAssignment = computed(() =>
    resolveTrendAssignment(presentedColumns.value, matrixOptions.value),
  )
  const availableTableTypes = computed<ReportTableType[]>(() => {
    // `null`, not `[]`: still-loading is "no real answer yet", same as a degraded catalog fetch.
    const definitions = !toValue(options.catalogSettled)
      ? null
      : toValue(options.catalogReady)
        ? toValue(options.catalogDefinitions)
        : null
    const allowed = new Set(toValue(options.allowedTableTypes) ?? DEFAULT_TABLE_TYPES)
    const visibleDimensions = presentedColumns.value
      .filter((column) => !column.numeric && !column.isImage && !column.invisible)
      .map((column) => column.field)
    const visibleMetrics = presentedColumns.value
      .filter((column) => column.numeric && !column.invisible)
      .map((column) => column.field)
    return getAvailableReportTableTypes(definitions, visibleDimensions, visibleMetrics).filter(
      (type) => allowed.has(type),
    )
  })
  const effectiveTableType = computed<ReportTableType>(() =>
    availableTableTypes.value.includes(presentation.tableType.value)
      ? presentation.tableType.value
      : (availableTableTypes.value[0] ?? 'table'),
  )
  const tableUnavailable = computed(
    () => toValue(options.catalogSettled) && availableTableTypes.value.length === 0,
  )

  watch(
    () => toValue(options.userFilters),
    (value) => {
      filterDraft.value = cloneFilters(value)
      activeUserFilters.value = cloneFilters(value)
    },
    { deep: true },
  )
  watch(
    () =>
      [
        viewConfigs.state.value,
        viewConfigs.configs.value,
        toValue(options.catalogSettled),
        toValue(options.id),
      ] as const,
    () => hydrateRemoteView(),
    { deep: true },
  )

  function hydrateRemoteView(): void {
    // An explicit saved-view choice this session (including an explicit clear) always wins over a
    // late-resolving fetch.
    if (userSelectedView.value) return
    if (viewConfigs.state.value !== 'ready') return
    const remote = viewConfigs.get(toValue(options.id))
    if (!remote) return
    options.onRemoteActiveChart(remote.activeChart)
    if (!table.hasPreference.value) table.hydrate(reportViewConfigToTablePreference(remote))
    const appliedPresentation = !presentation.hasPreference.value
    if (appliedPresentation) {
      presentation.hydrate({
        visibleFields: [
          ...remote.dimensions,
          ...remote.metrics,
          ...(Array.isArray(remote.mediaColumns) ? remote.mediaColumns : []),
        ],
        breakdownField: remote.breakdownColumn,
      })
    }
    // Reflect the auto-applied stable view in the toolbar's saved-view Select, unless a local named
    // view already owns the selection.
    if (appliedPresentation && localViews.selectedId.value === null) stableViewSelected.value = true
  }

  async function persistStableView(): Promise<void> {
    const workspaceId = workspace.workspaceId.value
    if (!toValue(options.enabled) || workspaceId === undefined) return
    const input = buildReportViewConfigUpsert({
      workspaceId,
      tableId: toValue(options.id),
      columns: presentedColumns.value,
      sort: table.sort.value,
      breakdownColumn: presentation.breakdownField.value,
      activeChart: toValue(options.activeChart),
    })
    const { workspaceId: _workspaceId, ...write } = input
    await viewConfigs.save(write)
  }

  function orderedColumns(fields: readonly string[]): TableColumn<ReportRow>[] {
    const byField = new Map(table.columns.value.map((column) => [column.field, column] as const))
    const all = [...fields, ...table.columns.value.map((column) => column.field)]
    return all.flatMap((field, index) => {
      const column = byField.get(field)
      return column && all.indexOf(field) === index ? [column] : []
    })
  }

  function applyCustomization(value: ReportTableCustomization): void {
    const canReorder = table.config.value.allowColumnReorder
    const order = canReorder
      ? table.setColumnOrder(orderedColumns(value.columnOrder))
      : table.columns.value.map((column) => column.field)
    presentation.apply(value)
    if (canReorder) options.onColumnOrder(order)
    options.onTableType(effectiveTableType.value)
    options.onBreakdown(presentation.breakdownField.value)
    options.onFrequency(presentation.frequency.value)
  }

  function updateTableType(value: ReportTableType): void {
    if (!availableTableTypes.value.includes(value)) return
    applyCustomization(currentCustomization({ tableType: value }))
  }

  function currentCustomization(
    patch: Partial<ReportTableCustomization>,
  ): ReportTableCustomization {
    return {
      visibleFields: presentation.visibleFields.value,
      columnOrder: table.columns.value.map((column) => column.field),
      frozenFields: presentation.frozenFields.value,
      expandedFields: presentation.expandedFields.value,
      tableType: effectiveTableType.value,
      breakdownField: presentation.breakdownField.value,
      frequency: presentation.frequency.value,
      totalRows: presentation.totalRows.value,
      ...patch,
    }
  }

  function applyFilters(value: ReportFilter[]): void {
    filterDraft.value = cloneFilters(value)
    activeUserFilters.value = cloneFilters(value)
    options.onFilters(cloneFilters(value))
  }

  function currentColumnFilters(value: readonly ReportFilter[]): ReportFilter[] {
    const available = new Set(table.columns.value.map((column) => column.field))
    return cloneFilters(value.filter((filter) => available.has(filter.field)))
  }

  async function saveFilter(value: { id?: string; name: string; filter: ReportFilter }) {
    try {
      if (value.id) await savedFilters.update(value.id, value)
      else await savedFilters.create(value)
      toast.success(t('reports.filters.saveSuccess'))
    } catch {
      toast.error(t('reports.filters.saveError'))
    }
  }

  async function removeFilter(id: string) {
    try {
      await savedFilters.remove(id)
      toast.success(t('reports.filters.deleteSuccess'))
    } catch {
      toast.error(t('reports.filters.saveError'))
    }
  }

  async function saveNamedView(value: ReportTableSavedViewDraft): Promise<void> {
    userSelectedView.value = true
    stableViewSelected.value = false
    try {
      const saved = localViews.save({ ...value, filters: currentColumnFilters(value.filters) })
      // Saving selects the snapshot. Apply the same snapshot immediately so the selected label,
      // request filters and rendered columns stay aligned before the one explicit remote write.
      applyFilters(saved.filters)
      applyCustomization(saved)
      await nextTick()
      await persistStableView()
      toast.success(t('reports.table.viewSaveSuccess'))
    } catch {
      toast.error(t('reports.table.viewSaveError'))
    }
  }

  function selectNamedView(id: string | null): void {
    userSelectedView.value = true
    if (id === STABLE_VIEW_SELECT_ID) return
    stableViewSelected.value = false
    const selected = localViews.select(id)
    if (!selected) return
    applyFilters(currentColumnFilters(selected.filters))
    applyCustomization(selected)
  }

  function savedViewDraft(view: ReportTableSavedViewV1): ReportTableSavedViewDraft {
    return {
      name: view.name,
      visibleFields: [...view.visibleFields],
      columnOrder: [...view.columnOrder],
      frozenFields: [...view.frozenFields],
      expandedFields: [...(view.expandedFields ?? [])],
      tableType: view.tableType,
      breakdownField: view.breakdownField,
      filters: cloneFilters(view.filters),
      ...(view.frequency === undefined ? {} : { frequency: view.frequency }),
      ...(view.totalRows === undefined ? {} : { totalRows: [...view.totalRows] }),
    }
  }

  function renameNamedView(value: { id: string; name: string }): void {
    const view = localViews.views.value.find((candidate) => candidate.id === value.id)
    if (!view) return
    try {
      const saved = localViews.save({ ...savedViewDraft(view), name: value.name }, value.id)
      if (localViews.selectedId.value === value.id) {
        applyFilters(currentColumnFilters(saved.filters))
        applyCustomization(saved)
      }
      toast.success(t('reports.table.viewRenameSuccess'))
    } catch {
      toast.error(t('reports.table.viewRenameError'))
    }
  }

  async function removeNamedView(id: string): Promise<void> {
    const view = localViews.views.value.find((candidate) => candidate.id === id)
    if (!view) return
    const accepted = await confirm({
      title: t('reports.table.deleteSavedViewTitle'),
      message: t('reports.table.deleteSavedViewMessage', { name: view.name }),
      confirmLabel: t('common.delete'),
      danger: true,
    })
    if (!accepted) return
    localViews.remove(id)
    toast.success(t('reports.table.viewDeleteSuccess'))
  }

  const savedViews = computed<{ id: string; name: string }[]>(() =>
    stableViewSelected.value && localViews.selectedId.value === null
      ? [
          { id: STABLE_VIEW_SELECT_ID, name: t('reports.table.defaultView') },
          ...localViews.views.value,
        ]
      : [...localViews.views.value],
  )
  const savedViewId = computed<string | null>(
    () => localViews.selectedId.value ?? (stableViewSelected.value ? STABLE_VIEW_SELECT_ID : null),
  )

  return {
    table,
    presentation,
    presentedColumns,
    matrixAssignment,
    trendAssignment,
    availableTableTypes,
    effectiveTableType,
    tableUnavailable,
    savedFilters,
    dropdownables: filterDropdownables.dropdownables,
    loadDropdownValues: filterDropdownables.loadDropdownValues,
    localViews,
    savedViews,
    savedViewId,
    filterDraft,
    activeUserFilters,
    customizeOpen,
    applyCustomization,
    updateTableType,
    updateBreakdown: (value: string | null) =>
      applyCustomization(currentCustomization({ breakdownField: value })),
    updateFrequency: (value: TrendTableFrequency) =>
      applyCustomization(currentCustomization({ frequency: value })),
    applyFilters,
    saveFilter,
    removeFilter,
    saveNamedView,
    selectNamedView,
    renameNamedView,
    removeNamedView,
    updateSort: (value: SortState) => {
      options.onSort(table.setSort(value))
    },
    updateColumnOrder: (value: TableColumn<ReportRow>[]) => {
      options.onColumnOrder(table.setColumnOrder(value))
    },
    updateColumnWidths: (value: TableColumn<ReportRow>[]) =>
      options.onColumnWidths(table.setColumnWidths(value)),
    updateFrozenField: (field: string, frozen: boolean) => {
      const next = new Set(presentation.frozenFields.value)
      if (frozen) next.add(field)
      else next.delete(field)
      applyCustomization(currentCustomization({ frozenFields: [...next] }))
    },
    updateExpandedField: (field: string, expanded: boolean) => {
      const next = new Set(presentation.expandedFields.value)
      if (expanded) next.add(field)
      else next.delete(field)
      applyCustomization(currentCustomization({ expandedFields: [...next] }))
    },
  }
}
